import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PieChart, BarChart3, Layers, TrendingUp, TrendingDown } from 'lucide-react';
import { SUPPORTED_CHAINS } from '../../types/multi-wallet';

export function AggregatedPortfolio() {
  // Mock aggregated data - will be replaced with real aggregated portfolio
  const portfolio = {
    totalValueUSD: 125847.32,
    change24h: 4523.15,
    change24hPercent: 3.72,
    chainBreakdown: [
      { chainId: 1, totalValue: 85230.50, percentage: 67.7 },
      { chainId: 137, totalValue: 25450.20, percentage: 20.2 },
      { chainId: 42161, totalValue: 10166.62, percentage: 8.1 },
      { chainId: 56, totalValue: 5000.00, percentage: 4.0 },
    ],
    tokenBreakdown: [
      { symbol: 'ETH', name: 'Ethereum', totalValue: 52340.50, percentage: 41.6, wallets: 3 },
      { symbol: 'WBTC', name: 'Wrapped Bitcoin', totalValue: 28450.00, percentage: 22.6, wallets: 2 },
      { symbol: 'NDH', name: 'Nardium', totalValue: 15230.82, percentage: 12.1, wallets: 4 },
      { symbol: 'USDC', name: 'USD Coin', totalValue: 12500.00, percentage: 9.9, wallets: 3 },
      { symbol: 'LINK', name: 'Chainlink', totalValue: 8326.00, percentage: 6.6, wallets: 2 },
      { symbol: 'Others', name: 'Other Tokens', totalValue: 9000.00, percentage: 7.2, wallets: 4 },
    ],
    topHoldings: [
      { symbol: 'ETH', balance: '23.45', value: 52340.50, change24h: 2.3 },
      { symbol: 'WBTC', balance: '0.658', value: 28450.00, change24h: 1.8 },
      { symbol: 'NDH', balance: '17,918.61', value: 15230.82, change24h: 5.2 },
      { symbol: 'USDC', balance: '12,500', value: 12500.00, change24h: 0 },
      { symbol: 'LINK', balance: '584.21', value: 8326.00, change24h: -1.5 },
    ],
  };

  const isPositive = portfolio.change24hPercent >= 0;

  return (
    <div className="space-y-6">
      {/* Total Portfolio Value */}
      <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/20">
        <CardContent className="p-6">
          <div className="text-center">
            <p className="text-sm text-slate-600 dark:text-slate-400">Valor Total del Portfolio</p>
            <p className="text-4xl font-bold text-slate-900 dark:text-slate-100 mt-2">
              ${portfolio.totalValueUSD.toLocaleString()}
            </p>
            <p className={`text-lg flex items-center gap-2 justify-center mt-2 ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
              {isPositive ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
              {isPositive ? '+' : ''}${portfolio.change24h.toLocaleString()} ({portfolio.change24hPercent}%) 24h
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chain Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-violet-500" />
              Distribucion por Cadena
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {portfolio.chainBreakdown.map((chain) => {
                const chainInfo = SUPPORTED_CHAINS[chain.chainId];
                return (
                  <div key={chain.chainId}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: chainInfo?.color || '#666' }}
                        />
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {chainInfo?.name || 'Unknown'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          ${chain.totalValue.toLocaleString()}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          ({chain.percentage}%)
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${chain.percentage}%`,
                          backgroundColor: chainInfo?.color || '#666',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Token Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-violet-500" />
              Distribucion por Token
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {portfolio.tokenBreakdown.map((token, index) => {
                const colors = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#6B7280'];
                return (
                  <div key={token.symbol}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: colors[index % colors.length] }}
                        />
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {token.symbol}
                        </span>
                        <span className="text-xs text-slate-500">
                          ({token.wallets} wallets)
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          ${token.totalValue.toLocaleString()}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          ({token.percentage}%)
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${token.percentage}%`,
                          backgroundColor: colors[index % colors.length],
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Holdings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-violet-500" />
            Top Holdings (Agregado)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <th className="pb-3">Token</th>
                  <th className="pb-3 text-right">Balance Total</th>
                  <th className="pb-3 text-right">Valor USD</th>
                  <th className="pb-3 text-right">Cambio 24h</th>
                </tr>
              </thead>
              <tbody>
                {portfolio.topHoldings.map((holding) => {
                  const isTokenPositive = holding.change24h >= 0;
                  return (
                    <tr
                      key={holding.symbol}
                      className="border-b border-slate-100 dark:border-slate-800 last:border-0"
                    >
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                            {holding.symbol.charAt(0)}
                          </div>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {holding.symbol}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 text-right font-mono text-slate-900 dark:text-slate-100">
                        {holding.balance}
                      </td>
                      <td className="py-3 text-right font-medium text-slate-900 dark:text-slate-100">
                        ${holding.value.toLocaleString()}
                      </td>
                      <td className="py-3 text-right">
                        <span className={`flex items-center gap-1 justify-end ${isTokenPositive ? 'text-green-500' : holding.change24h < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                          {holding.change24h > 0 && <TrendingUp className="h-3 w-3" />}
                          {holding.change24h < 0 && <TrendingDown className="h-3 w-3" />}
                          {holding.change24h > 0 ? '+' : ''}{holding.change24h}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
